import "./About.css";
import BRAND from "../../config/brand";

function About() {
  return (
    <div className="lp-page">

      <div className="lp-hero">
        <div className="lp-hero-tag">🎯 About</div>
        <h1>About {BRAND.name}</h1>
        <p>Structured learning for the modern self-learner.</p>
      </div>

      <div className="lp-container">

        <div className="lp-section">
          <h2>Our Mission</h2>
          <p>
            {BRAND.name} exists because free content alone doesn't create learners —
            structure does. The internet has more tutorials than anyone could ever watch.
            What's missing isn't content. It's a system that helps you actually finish.
          </p>
          <p>
            We built Learnlia to bring structure, accountability, and measurable progress
            to self-directed learning. One playlist at a time.
          </p>
        </div>

        <div className="lp-section">
          <h2>The Problem We Solve</h2>
          <p>
            Most self-learners start strong. They find a great YouTube playlist,
            watch a few videos with full focus — then life happens. They come back
            a week later, forget where they were, restart from the beginning, and
            eventually abandon the course entirely.
          </p>
          <p>
            This isn't a discipline problem. It's a systems problem.
            YouTube is built for discovery and entertainment, not for learning.
            There's no progress tracking, no structure, no accountability.
          </p>
          <div className="lp-callout">
            <strong>Learnlia is the layer YouTube is missing.</strong> We turn
            playlists into structured courses, track your real progress, and
            keep you consistent with streaks and roadmaps.
          </div>
        </div>

        <div className="lp-section">
          <h2>What We Offer</h2>
          <div className="about-features">
            {[
              { icon: "📚", title: "Playlist → Course", desc: "Convert any YouTube playlist into a tracked course with per-video progress." },
              { icon: "🗺️", title: "Learning Roadmaps", desc: "Build visual roadmaps with skills, courses, and project milestones in the right order." },
              { icon: "🔥", title: "Streak System", desc: "Daily learning streaks with XP, levels, and freeze credits to protect your streak." },
              { icon: "📊", title: "Analytics", desc: "Focus trends, consistency heatmaps, and completion data that show your real habits." },
              { icon: "📅", title: "Study Planner", desc: "Set a finish date and get a personalized daily watch target based on your pace." },
              { icon: "🎓", title: "Certificates", desc: "Earn verifiable certificates when you complete a course — shareable and downloadable." },
              { icon: "⚡", title: "Hardware Specialization", desc: "Deeply curated tracks for VLSI, Embedded Systems, PCB Design, and FPGA — an underserved niche we've made our priority." },

            ].map((f, i) => (
              <div className="about-feature-item" key={i}>
                <span className="about-feature-icon">{f.icon}</span>
                <div>
                  <strong>{f.title}</strong>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lp-section">
          <h2>Our Vision</h2>
          <p>
            We believe self-learning should be as powerful as formal education —
            but more flexible, more personal, and more accessible.
          </p>
          <p>
            The learners who succeed aren't necessarily the most talented.
            They're the ones who show up consistently. Learnlia is built to make
            that consistency easier — one video, one day, one streak at a time.
          </p>
        </div>

        <div className="lp-section">
          <h2>The Learnlia Special: Hardware Engineering</h2>
          <p>
            While most platforms focus solely on software, Learnlia is built with a deep commitment
            to the underserved hardware engineering community. We believe mastering hardware should
            be as structured and accessible as learning to code.
          </p>
          <p>
            We curate high-quality, free resources for complex fields like <strong>VLSI Design, Embedded Systems,
            PCB Prototyping, and FPGA Development</strong>. By sequencing these into clear, phase-based paths
            with AI-driven assessments, we bridge the gap between academic theory and industry readiness.
          </p>
        </div>

        <div className="lp-section">
          <h2>Built for College Learners</h2>
          <p>
            Learnlia started as a tool for students who learn on their own —
            from YouTube tutorials, free courses, and open resources.
            If you're teaching yourself to code, design, or build something,
            this is built for you.
          </p>
        </div>

      </div>
    </div>
  );
}

export default About;